import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Image,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { AirportIcon } from 'components/icons/airportIcon'
import { CalendarIcon } from 'components/icons/calendarIcon'
import { CustomSelect } from 'components/ui/customSelect'
import { addDays } from 'date-fns'
import { IncrementalSearchResponseData } from 'generated/graphql'
import { resetDateRange } from 'helper/date'
import { formatUtcDateStringInJST } from 'helper/dateFormat'
import {
  convertIncrementalSearchDataTypeToKeywordType,
  createOccupancies,
  defaultSearchHotelRoomParams,
  maxCheckinDaysInFuture,
  maxNightsOfStay,
  numOfAdults,
  numOfChildAges,
  numOfChildren,
  numOfRooms,
  searchHotelFormDefaultData,
  SearchHotelFormInput,
} from 'helper/searchHotelParams'
import { FC, useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { MdBusiness, MdLocationPin, MdMeetingRoom, MdOutlineChildCare, MdPerson, MdTrain } from 'react-icons/md'
import { AutocompleteInputField } from '../autocompleteInputField'
import { CustomDateRange } from '../customDateRange'
import { CustomHeading } from '../customHeading'
import { useSearchHotelForm } from './useSearchHotelForm'

const getIconByIncrementalSearchData = (data?: IncrementalSearchResponseData) => {
  switch (data?.type) {
    case 'property_content':
      return MdBusiness
    case 'metro_station':
    case 'train_station':
      return MdTrain
    case 'airport':
      return AirportIcon
    default:
      return MdLocationPin
  }
}

export type SearchHotelFormProps = {
  onSubmit: (data: SearchHotelFormInput) => void
  defaultValues?: Partial<SearchHotelFormInput>
  submitting?: boolean
  variant?: 'default' | 'mapSidebar'
}

export const SearchHotelForm: FC<SearchHotelFormProps> = ({ onSubmit, defaultValues, submitting, variant = 'default' }) => {
  const { register, handleSubmit, getValues, setValue, watch } = useForm<SearchHotelFormInput>({
    mode: 'onSubmit',
    defaultValues: {
      keyword: defaultValues?.keyword ?? searchHotelFormDefaultData.keyword(),
      keywordType: defaultValues?.keywordType ?? searchHotelFormDefaultData.keywordType(),
      coordinates: defaultValues?.coordinates,
      ...defaultSearchHotelRoomParams({
        checkin: defaultValues?.checkin,
        checkout: defaultValues?.checkout,
        occupancies: defaultValues?.occupancies,
      }),
    },
  })
  const { incrementalSearch, resetState, loading: incrementalLoading, data: incrementalSearchData } = useSearchHotelForm()
  const [selectedIncrementalSearchData, setSelectedIncrementalSearchData] = useState<IncrementalSearchResponseData>()
  const [incrementalSearchKeyword, setIncrementalSearchKeyword] = useState<string>(getValues('keyword'))
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false)
  const [isKeywordEdited, setIsKeywordEdited] = useState(false)
  const dateDisclosure = useDisclosure()
  const [errorMessage, setErrorMessage] = useState<string>()

  const _onSubmit = useCallback(
    async (data: SearchHotelFormInput) => {
      const canUseDefaultValues = !isKeywordEdited
      const coordinates = selectedIncrementalSearchData?.coordinates ?? (canUseDefaultValues ? data.coordinates : undefined)
      if (!coordinates) {
        setErrorMessage('入力欄に表示された候補を選択してください')
        return
      }
      setErrorMessage(undefined)
      const keywordType = selectedIncrementalSearchData
        ? convertIncrementalSearchDataTypeToKeywordType(selectedIncrementalSearchData.type)
        : data.keywordType

      onSubmit({
        ...data,
        keywordType,
        occupancies: data.occupancies.map(o => ({ adult: Number(o.adult), childAges: o.childAges })),
        coordinates,
        propertyContentId:
          keywordType === 'hotel'
            ? selectedIncrementalSearchData?.id ?? (canUseDefaultValues ? defaultValues?.propertyContentId : undefined)
            : undefined,
        regionId:
          selectedIncrementalSearchData?.type !== 'property_content'
            ? selectedIncrementalSearchData?.id ?? (canUseDefaultValues ? defaultValues?.regionId : undefined)
            : undefined,
        regionType:
          selectedIncrementalSearchData?.type === 'property_content'
            ? undefined
            : selectedIncrementalSearchData?.type ?? (canUseDefaultValues ? defaultValues?.regionType : undefined),
      })
      return false
    },
    [
      defaultValues?.propertyContentId,
      defaultValues?.regionId,
      defaultValues?.regionType,
      isKeywordEdited,
      onSubmit,
      selectedIncrementalSearchData,
    ]
  )

  // checkinが変わったときにcheckoutの日付を超えてたら、checkoutをinの翌日にする
  useEffect(() => {
    const checkout = resetDateRange(getValues('checkin'), getValues('checkout'))
    if (checkout) {
      setValue('checkout', checkout)
    }
  }, [watch('checkin'), getValues, setValue])

  // 検索ワードが変わったり、インクリメンタルサーチのBoxが表示されたら検索する
  useEffect(() => {
    if (!isAutocompleteOpen) {
      if (incrementalSearchKeyword.length === 0) resetState()
      return
    }
    incrementalSearch(incrementalSearchKeyword)
  }, [incrementalSearchKeyword, incrementalSearch, isAutocompleteOpen, resetState])

  useEffect(() => {
    if (defaultValues?.keyword) setValue('keyword', defaultValues.keyword)
    if (defaultValues?.keywordType) {
      setValue('keywordType', defaultValues.keywordType)
    }
    setIsKeywordEdited(false)
  }, [defaultValues])

  const onKeywordChanged = (text: string) => {
    setIncrementalSearchKeyword(text)
    setSelectedIncrementalSearchData(undefined)
    setIsKeywordEdited(true)
    setValue('keyword', '')
    setValue('coordinates', undefined)
  }

  const onDestinationSelected = (data: IncrementalSearchResponseData) => {
    setSelectedIncrementalSearchData(data)
    setIsKeywordEdited(false)
    setValue('keyword', data.name)
    setValue('keywordType', convertIncrementalSearchDataTypeToKeywordType(data.type))
    resetState()
  }

  const onRoomCountChanged = (value: string) => {
    const num = Number(value)
    if (!Number.isInteger(num)) return
    const occupancies = getValues('occupancies')
    const length = occupancies.length
    if (length < num) {
      setValue('occupancies', [...occupancies, ...createOccupancies(num - length)])
    } else if (length > num) {
      setValue('occupancies', occupancies.slice(0, num))
    }
  }

  const onChildCountChanged = (roomIndex: number, value: string) => {
    const num = Number(value)
    if (!Number.isInteger(num)) return
    const occupancies = getValues('occupancies')
    const childAges = occupancies[roomIndex].childAges
    occupancies[roomIndex].childAges = num > childAges.length ? [...Array(num)].map(() => 0) : childAges.slice(0, num)
    setValue('occupancies', [...occupancies])
  }

  if (variant === 'mapSidebar') {
    return (
      <form onSubmit={handleSubmit(_onSubmit)}>
        <Flex direction="column">
          <FormControl>
            <FormLabel fontSize="12px" lineHeight="14px" mb="6px">
              目的地
            </FormLabel>
            <AutocompleteInputField
              leftIcon={getIconByIncrementalSearchData(selectedIncrementalSearchData)}
              placeholder="目的地を入力"
              defaultKeyword={watch('keyword')}
              searching={incrementalLoading}
              incrementalSearchedData={incrementalSearchData}
              autocompletePopoverWidth="288px"
              onIsAutocompleteOpenChanged={isOpen => setIsAutocompleteOpen(isOpen)}
              onKeywordChanged={onKeywordChanged}
              onDataClicked={onDestinationSelected}
            />
          </FormControl>

          <Box position="relative" mt="28px">
            <Popover {...dateDisclosure}>
              <PopoverTrigger>
                <HStack spacing="12px" cursor="pointer" align="flex-start">
                  <FormControl>
                    <FormLabel fontSize="12px" lineHeight="14px" mb="6px">
                      チェックイン
                    </FormLabel>
                    <HStack bg="white" border="1px solid" borderColor="blue.600" h="48px" borderRadius="8px" px="10px" spacing="8px">
                      <Icon as={CalendarIcon} boxSize="20px" color="blue.500" />
                      <Text fontSize="14px">{formatUtcDateStringInJST(new Date(getValues('checkin')), 'M月d日')}</Text>
                    </HStack>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="12px" lineHeight="14px" mb="6px">
                      チェックアウト
                    </FormLabel>
                    <HStack bg="white" border="1px solid" borderColor="blue.600" h="48px" borderRadius="8px" px="10px" spacing="8px">
                      <Icon as={CalendarIcon} boxSize="20px" color="blue.500" />
                      <Text fontSize="14px">{formatUtcDateStringInJST(new Date(getValues('checkout')), 'M月d日')}</Text>
                    </HStack>
                  </FormControl>
                </HStack>
              </PopoverTrigger>
              <Portal>
                <PopoverContent w="774px">
                  <Box bg="white" w="774px" zIndex={10} borderRadius="8px" borderColor="gray.40" borderWidth="1px" overflow="hidden">
                    <CustomDateRange
                      defaultStartDate={getValues('checkin')}
                      defaultEndDate={getValues('checkout')}
                      maxDays={maxNightsOfStay}
                      maxStartDate={addDays(Date.now(), maxCheckinDaysInFuture)}
                      onChangeStartDate={value => setValue('checkin', value)}
                      onChangeEndDate={value => {
                        setValue('checkout', value)
                        dateDisclosure.onClose()
                      }}
                    />
                  </Box>
                </PopoverContent>
              </Portal>
            </Popover>
          </Box>

          <FormControl w="98px" mt="12px">
            <FormLabel fontSize="12px" lineHeight="14px" mb="6px">
              部屋数
            </FormLabel>
            <CustomSelect
              defaultValue={getValues('occupancies').length}
              icon={<Icon as={MdMeetingRoom} boxSize="20px" color="blue.500" />}
              onChange={event => onRoomCountChanged(event.target.value)}
            >
              {numOfRooms.map(num => (
                <option key={num} value={num}>
                  {num}室
                </option>
              ))}
            </CustomSelect>
          </FormControl>

          <Divider mt="28px" borderColor="gray.100" />

          <VStack align="stretch" mt="16px" spacing="16px">
            {watch('occupancies').map((occupancy, roomIndex) => (
              <Box key={`map_occupancy_${roomIndex}`}>
                <Flex align="flex-end" gap="8px">
                  <Text w="36px" mb="14px" fontSize="12px" whiteSpace="nowrap">
                    客室{roomIndex + 1}
                  </Text>
                  <FormControl flex="1">
                    <FormLabel fontSize="12px" lineHeight="14px" mb="6px">
                      大人
                    </FormLabel>
                    <CustomSelect
                      {...register(`occupancies.${roomIndex}.adult`)}
                      defaultValue={occupancy.adult}
                      icon={<Icon as={MdPerson} boxSize="20px" color="blue.500" />}
                    >
                      {numOfAdults.map(num => (
                        <option key={`map_adult_${num}`} value={num}>
                          {num}名
                        </option>
                      ))}
                    </CustomSelect>
                  </FormControl>
                  <FormControl flex="1">
                    <FormLabel fontSize="12px" lineHeight="14px" mb="6px">
                      子供
                    </FormLabel>
                    <CustomSelect
                      defaultValue={occupancy.childAges.length}
                      icon={<Icon as={MdOutlineChildCare} boxSize="20px" color="blue.500" />}
                      onChange={event => onChildCountChanged(roomIndex, event.target.value)}
                    >
                      {numOfChildren.map(num => (
                        <option key={`map_children_${num}`} value={num}>
                          {num}名
                        </option>
                      ))}
                    </CustomSelect>
                  </FormControl>
                </Flex>
                {occupancy.childAges.length > 0 && (
                  <VStack align="flex-end" mt="8px" spacing="8px">
                    {occupancy.childAges.map((childAge, childIndex) => (
                      <FormControl key={`map_child_age_${roomIndex}_${childIndex}`} w="calc((100% - 52px) / 2)">
                        <CustomSelect defaultValue={childAge} {...register(`occupancies.${roomIndex}.childAges.${childIndex}`)}>
                          {numOfChildAges.map(num => (
                            <option key={`map_child_age_option_${num}`} value={num}>
                              {num}歳
                            </option>
                          ))}
                        </CustomSelect>
                      </FormControl>
                    ))}
                  </VStack>
                )}
                {roomIndex < watch('occupancies').length - 1 && <Divider mt="16px" borderColor="gray.100" />}
              </Box>
            ))}
          </VStack>

          <Button
            type="submit"
            w="full"
            h="46px"
            minH="46px"
            mt="28px"
            fontSize="16px"
            lineHeight="20px"
            fontWeight="medium"
            isLoading={submitting}
          >
            検索
          </Button>
          {errorMessage && (
            <Text mt="-16px" fontSize="13px" color="red.400">
              {errorMessage}
            </Text>
          )}
        </Flex>
      </form>
    )
  }

  return (
    <Box position="relative">
      <Image src="/assets/pages/hotels/cloud1.svg" position="absolute" top="92px" left="-102px" w="152px" h="77px" zIndex={0} />
      <Image src="/assets/pages/hotels/cloud2.svg" position="absolute" bottom="0" right="-103px" w="99px" h="54px" zIndex={0} />
      <Image src="/assets/pages/hotels/cloud3.svg" position="absolute" bottom="-66px" left="478px" w="345px" h="58px" zIndex={0} />

      <Flex
        direction="column"
        rounded="lg"
        background="gray.10"
        boxShadow="0px 1px 7.007999897003174px 0px #000B264D"
        position="relative"
        zIndex={1}
        py={5}
        px={7}
      >
        <Flex mb={6}>
          <CustomHeading as="h5" underline>
            ホテルを探す
          </CustomHeading>
        </Flex>
        <form onSubmit={handleSubmit(_onSubmit)}>
          <Flex direction="column" justify="center">
            <HStack spacing={7} alignItems="flex-start">
              <FormControl>
                <FormLabel>目的地</FormLabel>
                <AutocompleteInputField
                  leftIcon={getIconByIncrementalSearchData(selectedIncrementalSearchData)}
                  placeholder="目的地を入力"
                  defaultKeyword={watch('keyword')}
                  searching={incrementalLoading}
                  incrementalSearchedData={incrementalSearchData}
                  onIsAutocompleteOpenChanged={isOpen => setIsAutocompleteOpen(isOpen)}
                  onKeywordChanged={onKeywordChanged}
                  onDataClicked={onDestinationSelected}
                />
              </FormControl>

              <Box position="relative">
                <Popover {...dateDisclosure}>
                  <PopoverTrigger>
                    <HStack spacing={3} cursor="pointer">
                      <FormControl>
                        <FormLabel>チェックイン</FormLabel>
                        <HStack background="white" border="1px solid" borderColor="blue.600" height={12} rounded="lg" w="152px" px={3}>
                          <Icon as={CalendarIcon} boxSize={6} color="blue.500" />
                          <Text fontSize={15}>{formatUtcDateStringInJST(new Date(getValues('checkin')), 'M月d日')}</Text>
                        </HStack>
                      </FormControl>
                      <FormControl>
                        <FormLabel>チェックアウト</FormLabel>
                        <HStack background="white" border="1px solid" borderColor="blue.600" height={12} rounded="lg" w="152px" px={3}>
                          <Icon as={CalendarIcon} boxSize={6} color="blue.500" />
                          <Text fontSize={15}>{formatUtcDateStringInJST(new Date(getValues('checkout')), 'M月d日')}</Text>
                        </HStack>
                      </FormControl>
                    </HStack>
                  </PopoverTrigger>
                  <PopoverContent>
                    <Box background="white" w="774px" zIndex={10} rounded="lg" borderColor="gray.40" borderWidth={1} overflow="hidden">
                      <CustomDateRange
                        defaultStartDate={getValues('checkin')}
                        defaultEndDate={getValues('checkout')}
                        maxDays={maxNightsOfStay}
                        maxStartDate={addDays(Date.now(), maxCheckinDaysInFuture)}
                        onChangeStartDate={value => setValue('checkin', value)}
                        onChangeEndDate={value => {
                          setValue('checkout', value)
                          dateDisclosure.onClose()
                        }}
                      />
                    </Box>
                  </PopoverContent>
                </Popover>
              </Box>

              <Box>
                <FormControl>
                  <FormLabel>部屋数</FormLabel>
                  <Box w="112px">
                    <CustomSelect
                      defaultValue={getValues('occupancies').length}
                      icon={<Icon as={MdMeetingRoom} boxSize={5} color="blue.500" />}
                      onChange={event => onRoomCountChanged(event.target.value)}
                    >
                      {numOfRooms.map(num => (
                        <option key={num} value={num}>
                          {num}室
                        </option>
                      ))}
                    </CustomSelect>
                  </Box>
                </FormControl>
              </Box>
              <VStack spacing={4}>
                {watch('occupancies').map((occupancy, i) => (
                  <Box key={`occupancy_${i}`}>
                    <Flex alignItems="flex-end">
                      <Text fontSize={14} lineHeight={'14px'} mr={2} mb={'13px'} whiteSpace="nowrap">
                        客室{i + 1}
                      </Text>
                      <HStack spacing={4}>
                        <FormControl>
                          <FormLabel>大人</FormLabel>
                          <Box w="112px">
                            <CustomSelect
                              {...register(`occupancies.${i}.adult`)}
                              defaultValue={occupancy.adult}
                              icon={<Icon as={MdPerson} boxSize={5} color="blue.500" />}
                            >
                              {numOfAdults.map(num => (
                                <option key={`adult_${num}`} value={num}>
                                  {num}名
                                </option>
                              ))}
                            </CustomSelect>
                          </Box>
                        </FormControl>
                        <FormControl>
                          <FormLabel>子供</FormLabel>
                          <Box w="112px">
                            <CustomSelect
                              defaultValue={occupancy.childAges?.length || 0}
                              icon={<Icon as={MdOutlineChildCare} boxSize={5} color="blue.500" />}
                              onChange={event => onChildCountChanged(i, event.target.value)}
                            >
                              {numOfChildren.map(num => (
                                <option key={`children_${num}`} value={num}>
                                  {num}名
                                </option>
                              ))}
                            </CustomSelect>
                          </Box>
                        </FormControl>
                      </HStack>
                    </Flex>
                    {occupancy.childAges && (
                      <VStack alignItems="flex-end" mt={2}>
                        {occupancy.childAges.map((childAge, j) => {
                          return (
                            <FormControl key={`childAge_${i}_${j}`} w="112px">
                              <Box>
                                <CustomSelect defaultValue={childAge} {...register(`occupancies.${i}.childAges.${j}`)}>
                                  {numOfChildAges.map(num => (
                                    <option key={`child_ages_${num}`} value={num}>
                                      {num}歳
                                    </option>
                                  ))}
                                </CustomSelect>
                              </Box>
                            </FormControl>
                          )
                        })}
                      </VStack>
                    )}
                  </Box>
                ))}
              </VStack>
            </HStack>

            <Flex mt={8} direction="column" justify="center" align="center">
              <Button type="submit" fontSize={16} w="284px" isLoading={submitting}>
                検索
              </Button>
              {errorMessage && (
                <Text mt="4" textColor="red.400">
                  {errorMessage}
                </Text>
              )}
            </Flex>
          </Flex>
        </form>
      </Flex>
    </Box>
  )
}
